import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToasterService } from '@lib/components/toaster/toaster.service';

export interface ApiErrorResponse {
    errors?: Array<{
        errorCode: string | null;
        message: string;
    }>;
    message?: string;
    error?: string;
    errorDescription?: string;
    errorCode?: number | string;
    localizedMessage?: string;
    exception?: string;
    stackTrace?: any[];
    suppressed?: any[];
}

export interface ErrorHandlingOptions {
    showFriendlyMessage?: boolean;
    fallbackToApiMessage?: boolean;
    defaultMessage?: string;
    context?: string; // Additional context for more specific error mapping
}

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlerService {
    /**
     * Extracts the most relevant error message from an API error response.
     * Uses the same logic as the interceptor for consistency.
     */
    extractErrorMessage(error: HttpErrorResponse): string {
        let errorMessage = 'An error occurred. Please try again.';

        // Handle network errors first
        if (error?.status === 0 || error?.error instanceof ErrorEvent) {
            return 'Network error: Please check your internet connection and try again.';
        }

        if (error?.error) {
            const apiError = error.error as ApiErrorResponse;

            // Check for errorDescription first (most specific error message)
            if (apiError.errorDescription && typeof apiError.errorDescription === 'string') {
                errorMessage = apiError.errorDescription;
            }
            // Check for errors array (common pattern in your API)
            else if (apiError.errors && apiError.errors.length > 0 && apiError.errors[0]?.message) {
                errorMessage = apiError.errors[0].message;
            }
            // Check for direct message property
            else if (apiError.message && typeof apiError.message === 'string') {
                errorMessage = apiError.message;
            }
            // Check for localizedMessage
            else if (apiError.localizedMessage && typeof apiError.localizedMessage === 'string') {
                errorMessage = apiError.localizedMessage;
            }
            // Check for error property
            else if (apiError.error && typeof apiError.error === 'string') {
                errorMessage = apiError.error;
            }
        }

        // Ensure we always return a valid string (never null or undefined)
        return errorMessage || 'An error occurred. Please try again.';
    }

    /**
     * Gets a user-friendly error message with fallback to actual API message.
     * This is the main method components should use for error handling.
     */
    getErrorMessage(error: HttpErrorResponse, options: ErrorHandlingOptions = {}): string {
        const {
            showFriendlyMessage = true,
            fallbackToApiMessage = true,
            defaultMessage = 'An error occurred. Please try again.',
            context = '',
        } = options;

        // Extract the actual error message from the API response
        const apiMessage = this.extractErrorMessage(error);

        // If we don't want friendly messages, return the API message directly
        if (!showFriendlyMessage) {
            return apiMessage || defaultMessage;
        }

        // Try to get a friendly message
        const friendlyMessage = this.getFriendlyErrorMessage(error, context);

        // If we have a friendly message that's different from the API message, use it
        if (friendlyMessage && friendlyMessage.trim() !== '' && friendlyMessage !== apiMessage) {
            return friendlyMessage;
        }

        // If fallback is enabled and we have an API message, use it
        if (fallbackToApiMessage && apiMessage && apiMessage.trim() !== '' && apiMessage !== defaultMessage) {
            return apiMessage;
        }

        // Return the default message as last resort (ensure it's never null/undefined)
        return defaultMessage || 'An error occurred. Please try again.';
    }

    /**
     * Checks if an error is a specific type of error.
     * Useful for conditional error handling in components.
     */
    isSpecificError(error: HttpErrorResponse, errorMessage: string): boolean {
        const extractedMessage = this.extractErrorMessage(error);
        return extractedMessage.toLowerCase().includes(errorMessage.toLowerCase());
    }

    /**
     * Checks if the error is a network-related error.
     * Useful for showing different UI states for network issues.
     */
    isNetworkError(error: HttpErrorResponse): boolean {
        return (
            error.status === 0 ||
            error.error instanceof ErrorEvent ||
            error.message?.toLowerCase().includes('network') ||
            error.message?.toLowerCase().includes('connection') ||
            error.message?.toLowerCase().includes('timeout')
        );
    }

    /**
     * Gets a user-friendly error message based on error type and context.
     * Enhanced with more comprehensive error mappings and context awareness.
     */
    getFriendlyErrorMessage(error: HttpErrorResponse, context: string = ''): string {
        const extractedMessage = this.extractErrorMessage(error);

        // Comprehensive error mappings with context awareness
        const errorMappings: { [key: string]: string } = {
            // Authentication & Authorization
            'user not found': 'The email address you entered is not registered with us.',
            'user does not exists': 'The email address you entered is not registered with us.',
            'invalid credentials': 'The email or password you entered is incorrect.',
            'email already exists': 'An account with this email address already exists.',
            'password is required': 'Please enter your password.', // NOSONAR
            'email is required': 'Please enter your email address.',
            unauthorized: 'You are not authorized to perform this action.',
            forbidden: 'Access denied. You do not have permission to perform this action.',

            // Registration & Enrollment specific
            'email doesnt match the signup email':
                'The email address you entered does not match the email used for registration.',
            "email doesn't match the signup email":
                'The email address you entered does not match the email used for registration.',
            'enrollment already exists': 'You have already enrolled in this course.',
            'enrollment not found': 'Your enrollment details could not be found.',
            'course not found': 'The requested course could not be found.',
            'program not found': 'The requested program could not be found.',
            'invalid course id': 'The course ID is invalid or has expired.',
            'registration closed': 'Registration for this course has closed.',
            'registration not open': 'Registration for this course is not yet open.',

            // File Upload & Document
            'file too large': 'The file you uploaded is too large. Please choose a smaller file.',
            'invalid file type': 'The file type is not supported. Please upload a valid file.',
            'file upload failed': 'Failed to upload the file. Please try again.',
            'document required': 'Please upload the required document.',
            'certificate required': 'Please upload your certificate or marksheet.',

            // Payment & Fees
            'payment failed': 'Payment could not be processed. Please try again.',
            'payment required': 'Payment is required to complete enrollment.',
            'invalid payment': 'The payment information provided is invalid.',
            'fee calculation error': 'Unable to calculate fees. Please contact support.',

            // Course & Learning
            'feedback already submitted': 'You have already submitted feedback for this course.',
            'course completed': 'This course has already been completed.',
            'course not available': 'This course is currently not available.',
            'prerequisites not met': 'You do not meet the prerequisites for this course.',

            // Network & Connection
            'network error': 'Please check your internet connection and try again.',
            'connection failed': 'Unable to connect to the server. Please check your internet connection.',
            timeout: 'Request timed out. Please try again.',
            'server error': 'A server error occurred. Please try again later.',
            'service unavailable': 'The service is temporarily unavailable. Please try again later.',

            // Validation & Form
            'validation failed': 'Please check your input and try again.',
            'required field missing': 'Please fill in all required fields.',
            'invalid email format': 'Please enter a valid email address.',
            'invalid phone number': 'Please enter a valid phone number.',
            'invalid date': 'Please enter a valid date.',

            // Generic fallbacks
            'bad request': 'Invalid request. Please check your input and try again.',
            'not found': 'The requested resource could not be found.',
            'internal server error': 'A server error occurred. Please try again later.',
            'service error': 'A service error occurred. Please try again later.',
        };

        // Context-specific mappings
        const contextMappings: { [key: string]: { [key: string]: string } } = {
            registration: {
                'email doesnt match': 'The email address you entered does not match the email used for registration.',
                'enrollment failed': 'Failed to submit your enrollment. Please try again.',
                'form validation': 'Please check all the required fields and try again.',
            },
            login: {
                'invalid credentials': 'The email or password you entered is incorrect.',
                'user does not exists': 'The email address you entered is not registered with us.',
                'user not found': 'The email address you entered is not registered with us.',
                'account locked': 'Your account has been locked. Please contact support.',
            },
            payment: {
                'payment failed': 'Payment could not be processed. Please try again or use a different payment method.',
                'insufficient funds': 'Insufficient funds. Please check your payment method.',
            },
            file: {
                'upload failed': 'File upload failed. Please try again.',
                'invalid format': 'The file format is not supported. Please upload a valid file.',
            },
        };

        const lowerMessage = extractedMessage.toLowerCase();
        const lowerContext = context.toLowerCase();

        // First, try context-specific mappings
        if (contextMappings[lowerContext]) {
            for (const [key, friendlyMessage] of Object.entries(contextMappings[lowerContext])) {
                if (lowerMessage.includes(key)) {
                    return friendlyMessage;
                }
            }
        }

        // Then try general mappings
        for (const [key, friendlyMessage] of Object.entries(errorMappings)) {
            if (lowerMessage.includes(key)) {
                return friendlyMessage;
            }
        }

        // If no friendly message found, return null to indicate fallback should be used
        return '';
    }

    /**
     * Determines if an error should be handled globally or locally.
     * Returns true if the error should be handled by the component.
     */
    shouldHandleLocally(error: HttpErrorResponse): boolean {
        // Handle network errors globally
        if (error.status === 0 || error.error instanceof ErrorEvent) {
            return false;
        }

        // Handle authentication errors globally
        if (error.status === 401 || error.status === 403) {
            return false;
        }

        // Handle 404 errors globally
        if (error.status === 404) {
            return false;
        }

        // Handle 500+ server errors globally
        if (error.status >= 500) {
            return false;
        }

        // Allow components to handle 4xx errors locally
        return error.status >= 400 && error.status < 500;
    }

    /**
     * Convenience method for components to handle errors with toaster service.
     * This method combines error message extraction with toaster display.
     */
    handleErrorWithToaster(
        error: HttpErrorResponse,
        toasterService: ToasterService,
        options: ErrorHandlingOptions = {},
    ): void {
        const errorMessage = this.getErrorMessage(error, options);

        toasterService.error(errorMessage, {
            duration: options.context === 'registration' ? 5000 : 4000, // Longer duration for registration errors
        });
    }
}
